import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, errMessage } from "../lib/api";
import { t } from "../lib/i18n";
import Seo from "../components/Seo";
import { SectionHead } from "../components/Blocks";

const INTERESTS = [
  "ambassador",
  "creator_influencer",
  "model_talent",
  "photographer",
  "videographer_filmmaker",
  "event_participation",
  "partner_business",
  "other_creative",
];
const LANGUAGES = ["fr", "en", "es", "autre"];

export default function Join() {
  const [params] = useSearchParams();
  const preset = params.get("interet");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    city: "",
    email: "",
    phone: "",
    instagram: "",
    tiktok: "",
    other_social_profile: "",
    approximate_audience_size: "",
    short_introduction: "",
    relevant_experience: "",
    portfolio_url: "",
    availability: "",
    honeypot: "",
  });
  const [interests, setInterests] = useState(preset && INTERESTS.includes(preset) ? [preset] : []);
  const [languages, setLanguages] = useState(["fr"]);
  const [consent, setConsent] = useState(false);
  const [adult, setAdult] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const canSubmit = adult && consent && interests.length > 0 && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.first_name.trim() || !form.last_name.trim() || !form.city.trim()) {
      setError("Indiquez votre prénom, votre nom et votre ville.");
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      setError("Indiquez au moins un courriel ou un téléphone.");
      return;
    }
    if (!adult) {
      setError("Vous devez confirmer avoir 18 ans ou plus.");
      return;
    }
    if (!consent) {
      setError("Vous devez consentir à être contacté.");
      return;
    }
    if (interests.length === 0) {
      setError("Sélectionnez au moins un intérêt.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        approximate_audience_size: form.approximate_audience_size
          ? Number(form.approximate_audience_size)
          : null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        instagram: form.instagram || null,
        tiktok: form.tiktok || null,
        other_social_profile: form.other_social_profile || null,
        short_introduction: form.short_introduction || null,
        relevant_experience: form.relevant_experience || null,
        portfolio_url: form.portfolio_url || null,
        availability: form.availability || null,
        interests,
        languages,
        consent_to_be_contacted: consent,
        confirms_age_18_plus: adult,
      };
      await api.post("/applications", payload);
      setDone(true);
      toast.success("Candidature envoyée");
    } catch (err) {
      const msg = errMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="join-success" ref={(el) => el?.scrollIntoView({ block: "center" })}>
        <CheckCircle2 className="mx-auto text-[#FF2E63]" size={48} />
        <h1 className="vb-display text-4xl lg:text-5xl font-black mt-8">Merci !</h1>
        <p className="mt-6 text-neutral-400">
          Votre candidature a été reçue. L'équipe Vytali vous contactera pour la suite. Aucune
          information n'est publiée publiquement.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="join-page" className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
      <Seo
        title="Rejoindre VytaliBoots"
        description="Postulez pour devenir Ambassadeur, Créateur, Talent, Photographe, Vidéaste ou Partenaire de VytaliBoots. 18 ans et plus."
        path="/rejoindre"
        kind="home"
      />
      <SectionHead as="h1" label="Recrutement" title="Rejoindre VytaliBoots">
        Choisissez un ou plusieurs intérêts. Seuls le nom, la ville, un moyen de contact et un
        intérêt sont requis — le reste est facultatif. Aucun nombre d'abonnés requis. Aucun
        parcours commercial imposé.
      </SectionHead>

      <form onSubmit={submit} className="space-y-14" data-testid="join-form" noValidate>
        <input
          type="text"
          value={form.honeypot}
          onChange={set("honeypot")}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          data-testid="join-honeypot"
          className="hidden"
        />

        <fieldset className="grid md:grid-cols-2 gap-8">
          <div>
            <label className="vb-label">Prénom *</label>
            <input className="vb-input" required maxLength={60} value={form.first_name} onChange={set("first_name")} data-testid="join-first-name" />
          </div>
          <div>
            <label className="vb-label">Nom *</label>
            <input className="vb-input" required maxLength={60} value={form.last_name} onChange={set("last_name")} data-testid="join-last-name" />
          </div>
          <div>
            <label className="vb-label">Ville *</label>
            <input className="vb-input" required maxLength={80} value={form.city} onChange={set("city")} data-testid="join-city" />
          </div>
          <div>
            <label className="vb-label">Courriel</label>
            <input className="vb-input" type="email" value={form.email} onChange={set("email")} data-testid="join-email" />
          </div>
          <div>
            <label className="vb-label">Téléphone</label>
            <input className="vb-input" maxLength={30} value={form.phone} onChange={set("phone")} data-testid="join-phone" />
          </div>
          <div className="md:col-span-2 -mt-4 text-xs text-neutral-500" data-testid="join-contact-hint">
            Indiquez au moins un moyen de contact : courriel ou téléphone.
          </div>
          <div>
            <label className="vb-label">Disponibilité</label>
            <input className="vb-input" maxLength={300} value={form.availability} onChange={set("availability")} data-testid="join-availability" />
          </div>
        </fieldset>

        <fieldset>
          <legend className="vb-label mb-5">Intérêts * (un ou plusieurs)</legend>
          <div className="flex flex-wrap gap-3" data-testid="join-interests">
            {INTERESTS.map((i) => (
              <button
                key={i}
                type="button"
                className="vb-chip"
                data-active={interests.includes(i)}
                data-testid={`join-interest-${i}`}
                onClick={() => toggle(interests, setInterests, i)}
              >
                {t(`interests.${i}`, i)}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="grid md:grid-cols-2 gap-8">
          <div>
            <label className="vb-label">Instagram</label>
            <input className="vb-input" maxLength={120} value={form.instagram} onChange={set("instagram")} data-testid="join-instagram" />
          </div>
          <div>
            <label className="vb-label">TikTok</label>
            <input className="vb-input" maxLength={120} value={form.tiktok} onChange={set("tiktok")} data-testid="join-tiktok" />
          </div>
          <div>
            <label className="vb-label">Autre réseau social</label>
            <input className="vb-input" maxLength={200} value={form.other_social_profile} onChange={set("other_social_profile")} data-testid="join-other-social" />
          </div>
          <div>
            <label className="vb-label">Taille d'audience approximative (optionnel)</label>
            <input className="vb-input" type="number" min="0" value={form.approximate_audience_size} onChange={set("approximate_audience_size")} data-testid="join-audience" />
          </div>
          <div className="md:col-span-2">
            <label className="vb-label">Portfolio (optionnel)</label>
            <input className="vb-input" maxLength={300} value={form.portfolio_url} onChange={set("portfolio_url")} data-testid="join-portfolio" />
          </div>
        </fieldset>

        <fieldset>
          <legend className="vb-label mb-5">Langues</legend>
          <div className="flex flex-wrap gap-3" data-testid="join-languages">
            {LANGUAGES.map((l) => (
              <button
                key={l}
                type="button"
                className="vb-chip"
                data-active={languages.includes(l)}
                data-testid={`join-language-${l}`}
                onClick={() => toggle(languages, setLanguages, l)}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-8">
          <div>
            <label className="vb-label">Courte présentation (facultatif)</label>
            <textarea
              className="vb-input min-h-[120px] resize-y"
              maxLength={1500}
              value={form.short_introduction}
              onChange={set("short_introduction")}
              data-testid="join-introduction"
            />
          </div>
          <div>
            <label className="vb-label">Expérience pertinente (facultatif)</label>
            <textarea
              className="vb-input min-h-[100px] resize-y"
              maxLength={1500}
              value={form.relevant_experience}
              onChange={set("relevant_experience")}
              data-testid="join-experience"
            />
          </div>
        </div>

        <div className="space-y-5 border-t border-white/10 pt-10">
          <label className="flex items-start gap-4 cursor-pointer">
            <input
              type="checkbox"
              checked={adult}
              onChange={(e) => setAdult(e.target.checked)}
              data-testid="join-age-confirm"
              className="mt-1 w-5 h-5 accent-[#FF2E63]"
            />
            <span className="text-sm text-neutral-300">
              Je confirme que j'ai 18 ans ou plus. <span className="text-[#FF2E63]">*</span>
            </span>
          </label>
          <label className="flex items-start gap-4 cursor-pointer">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="join-consent"
              className="mt-1 w-5 h-5 accent-[#FF2E63]"
            />
            <span className="text-sm text-neutral-300">
              Je consens à être contacté par l'équipe Vytali au sujet de ma candidature.{" "}
              <span className="text-[#FF2E63]">*</span>
            </span>
          </label>
        </div>

        {error && (
          <div className="text-sm text-[#FF3B30]" data-testid="join-error">
            {error}
          </div>
        )}

        <button type="submit" disabled={!canSubmit} data-testid="join-submit" className="vb-btn vb-btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
          {submitting ? "Envoi…" : "Envoyer ma candidature"}
        </button>
      </form>
    </div>
  );
}
